import { ArrowUpRight, Users, Mic, CalendarDays, Globe } from "lucide-react";
import { CornerMarks, StatusLED } from "@/components/infra";

const SPEAKERS = [
  { name: "Carrie Charles", role: "CEO, Broadstaff" },
  { name: "Michael Nimeth", role: "CRO, Salute" },
  { name: "Brooke Petosa", role: "VP, Tradition Energy" },
  { name: "Indigo Pinto", role: "Co-Founder, IBB Solutions" },
  { name: "Santiago Suinaga", role: "SVP, Stream Data Centers" },
];

export default function Partnership() {
  return (
    <section
      id="partnership"
      data-testid="partnership-section"
      className="border-b hairline bg-[#F4F5F7] relative"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-32">
        {/* Header */}
        <div className="grid grid-cols-12 gap-y-12 gap-x-6 mb-12">
          <div className="col-span-12 lg:col-span-5">
            <div data-testid="partnership-section-label" className="font-mono-label text-[11px] text-[#0F1117] mb-6 flex items-center gap-3">
              <StatusLED />
              / Recent partnership
            </div>
            <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-extrabold text-[#0F1117] text-balance">
              Built in <span className="text-chrome">lockstep</span> with the
              industry.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7">
            <p className="text-base sm:text-lg text-[#3D4049] leading-relaxed max-w-xl">
              DCYB is in formal partnership with{" "}
              <strong className="text-[#0F1117] font-semibold">
                Infrastructure Masons (iMasons)
              </strong>{" "}
              — the global community of professionals building the digital
              infrastructure of the modern world. Together, we bring the next
              generation directly into rooms with the people running it.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Card 1: iMasons */}
          <article
            data-testid="partnership-imasons-card"
            className="chrome-surface relative p-8 md:p-10 flex flex-col"
          >
            <CornerMarks color="#A9ADB4" />
            <div className="flex items-start justify-between mb-8">
              <span className="font-mono-label text-[10px] text-[#71747B]">
                / Previous collaboration
              </span>
              <span className="font-mono-label text-[10px] text-[#0F1117] flex items-center gap-1.5">
                <Globe size={11} />
                imasons.org
              </span>
            </div>

            {/* iMasons logo */}
            <div className="mb-6 mt-2">
              <img
                src="/imasons-logo.png"
                alt="Infrastructure Masons"
                className="h-16 md:h-20 w-auto object-contain"
                draggable="false"
              />
            </div>

            <h3 className="font-display text-2xl md:text-3xl font-bold text-[#0F1117] tracking-tight mb-2">
              Infrastructure Masons
            </h3>
            <p className="text-[15px] text-[#52555B] leading-relaxed mb-6 max-w-md">
              The global non-profit community of executives and professionals
              advancing the digital infrastructure industry. Members include
              hyperscalers, colos, OEMs, A&E firms, and operators across six
              continents.
            </p>

            {/* Stat strip */}
            <div className="grid grid-cols-2 gap-px bg-[#D2D5DA] border hairline mb-8">
              <div className="bg-white p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Users size={12} className="text-[#0F1117]" />
                  <span className="font-mono-label text-[10px] text-[#71747B]">
                    Members
                  </span>
                </div>
                <div className="font-display text-2xl font-extrabold text-[#0F1117]">
                  19,665+
                </div>
              </div>
              <div className="bg-white p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Globe size={12} className="text-[#0F1117]" />
                  <span className="font-mono-label text-[10px] text-[#71747B]">
                    Reach
                  </span>
                </div>
                <div className="font-display text-2xl font-extrabold text-[#0F1117]">
                  Global
                </div>
              </div>
            </div>

            <a
              href="https://imasons.org/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="imasons-link"
              className="chrome-btn-dark inline-flex w-fit items-center gap-2 font-mono-label text-[12px] px-6 py-4 mt-auto"
            >
              Visit imasons.org
              <ArrowUpRight size={16} />
            </a>
          </article>

          {/* Card 2: Featured collaboration */}
          <article
            data-testid="partnership-event-card"
            className="chrome-surface relative p-8 md:p-10 flex flex-col bg-gradient-to-b from-white to-[#F2F3F5]"
          >
            <CornerMarks color="#A9ADB4" />
            <div className="flex items-start justify-between mb-6">
              <span className="font-mono-label text-[10px] text-[#71747B]">
                / Featured collaboration
              </span>
              <span className="font-mono-label text-[10px] text-[#0F1117] flex items-center gap-1.5">
                <StatusLED color="#71747B" />
                Completed
              </span>
            </div>

            <div className="font-mono-label text-[10px] text-[#71747B] mb-2">
              iM EMERGING PROFESSIONALS · DCYB
            </div>
            <h3 className="font-display text-3xl md:text-4xl font-extrabold text-[#0F1117] tracking-tight mb-2 leading-tight">
              Future You — <span className="text-chrome">Build It Now.</span>
            </h3>
            <p className="text-[14px] text-[#52555B] leading-relaxed mb-6 max-w-md">
              A direct-access session for those newer to the industry. Live panel
              with five senior leaders, followed by 10-minute breakout rooms — the
              kind of access you don&apos;t get from the outside.
            </p>

            {/* Event meta */}
            <div className="grid grid-cols-3 gap-px bg-[#D2D5DA] border hairline mb-6">
              <Meta icon={CalendarDays} k="Date" v="Apr 16" />
              <Meta icon={Globe} k="Format" v="Virtual" />
              <Meta icon={Mic} k="Speakers" v="5 leaders" />
            </div>

            {/* Speakers */}
            <div className="border hairline bg-white">
              <div className="px-4 py-2.5 border-b hairline">
                <span className="font-mono-label text-[10px] text-[#71747B]">
                  / Featured panel
                </span>
              </div>
              <ul className="divide-y divide-[#E3E5E9]">
                {SPEAKERS.map((s) => (
                  <li key={s.name} className="px-4 py-2.5 flex items-center justify-between gap-3">
                    <span className="text-[13px] font-medium text-[#0F1117] shrink-0">
                      {s.name}
                    </span>
                    <span className="text-[12px] text-[#5F6168] text-right min-w-0">
                      {s.role}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </article>
        </div>

        {/* Quote band */}
        <div className="mt-12 chrome-surface relative p-6 md:p-8">
          <div className="flex items-start gap-6">
            <span className="font-display text-6xl leading-none font-extrabold text-chrome shrink-0" aria-hidden>
              &ldquo;
            </span>
            <div>
              <p className="font-display text-lg md:text-xl text-[#0F1117] leading-snug max-w-3xl">
                The next generation of digital infrastructure won&apos;t be built
                in keynote rooms — it&apos;ll be built in the small-group sessions
                where seniors hand the playbook to the people coming up.
              </p>
              <div className="mt-4 font-mono-label text-[10px] text-[#71747B]">
                DCYB · iM Emerging Professionals
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Meta({ icon: Icon, k, v }) {
  return (
    <div className="bg-white px-1 py-3 sm:p-3 text-center min-w-0">
      <div className="flex items-center justify-center gap-1.5 mb-1">
        <Icon size={11} className="text-[#0F1117]" />
        <span className="font-mono-label text-[9px] text-[#71747B]">{k}</span>
      </div>
      <div className="font-display text-[10px] min-[360px]:text-[11px] min-[400px]:text-xs sm:text-base font-bold text-[#0F1117] whitespace-nowrap">{v}</div>
    </div>
  );
}
